/**
 * Authentication service
 * Handles user authentication, profile management, and password operations
 */
import APIClient from '../apiClient';
import { fetchMe } from './meService';

export const signup = async (userData) => {
  try {
    const response = await APIClient.post(`/authentication/signup`, userData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProfile = async () => {
  const user = await fetchMe();
  if (!user) throw new Error('Session expired. Please login again.');
  return { success: true, data: { user } };
};

export const logoutUser = async () => {
  try {
    await APIClient.post('/authentication/logout');
  } catch (e) {
    // Ignore network/500s — the cookie is cleared server-side on success; the
    // caller also clears context state regardless.
  }
};

/**
 * Permanently delete the signed-in user's account.
 *
 * The password is REQUIRED and must reach the server: it is what re-authorises
 * a destructive action beyond merely holding a session. This function used to
 * take no parameters and post an empty body, silently dropping the password the
 * caller passed — so every attempt failed validation with 400, and the server
 * had nothing to verify even if it had tried to.
 *
 * @param {{ password: string }} credentials
 */
export const deleteAccount = async ({ password } = {}) => {
  try {
    const response = await APIClient.post('/authentication/delete-account', { password });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Ask for a fresh email-verification link.
 *
 * The server answers identically whether or not an unverified account exists,
 * so callers must not try to infer anything from a success response.
 */
export const resendVerificationEmail = async (email) => {
  try {
    const response = await APIClient.post('/authentication/resend-verification', { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const editProfile = async (profileData) => {
  try {
    const response = await APIClient.put(`/authentication/edit-profile`, profileData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const requestPasswordReset = async (email) => {
  try {
    const response = await APIClient.post('/authentication/forgetPassword', { email });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (id, token, newPassword, confirmNewPassword) => {
  try {
    // Pass the id and token as query parameters, and the passwords in the body
    const response = await APIClient.post(`/authentication/reset-password`, {
      newPassword,
      confirmNewPassword,
    }, {
      params: {
        id,
        token,
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const requestLoggedInPasswordReset = async () => {
  try {
    const response = await APIClient.post('/authentication/reset', {});
    return response.data;
  } catch (error) {
    throw error;
  }
};

